import React, { useState, useEffect, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useVocab } from "@/lib/vocab";
import ChatImage from "@/components/ChatImage";
import VehicleDialog from "@/components/VehicleDialog";
import SubscriptionBadge, { clientSubscriptionSummary } from "@/components/SubscriptionBadge";
import RideDialog from "@/components/RideDialog";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { AlertTriangle, Camera, Loader2, Plus } from "lucide-react";
import { fmtDateTime, missionLabel, money } from "@/lib/helpers";

const toLocalInput = (iso) => {
  if (!iso) return "";
  const d = new Date(iso);
  return new Date(d.getTime() - d.getTimezoneOffset() * 60000).toISOString().slice(0, 16);
};

// Transport (lot T1a) : la fenêtre de course (RideDialog). Conciergerie : la fenêtre de mission ci-dessous, inchangée.
export default function MissionDialog(props) {
  const voc = useVocab();
  return voc.business === "transport" ? <RideDialog {...props} /> : <ConciergerieMissionDialog {...props} />;
}

function ConciergerieMissionDialog({ open, onOpenChange, mission, defaultStart, onSaved }) {
  const { settings } = useSettings();
  const voc = useVocab();
  const conciergerie = voc.business === "conciergerie"; // abonnements et récupération à domicile : conciergerie seulement
  const [clients, setClients] = useState([]);
  const [vehicles, setVehicles] = useState([]);
  const [subs, setSubs] = useState([]); // abonnements (le plus ancien en premier)
  const [available, setAvailable] = useState(null); // null = liste pas encore reçue
  const [assigned, setAssigned] = useState(null); // employé déjà assigné à la mission ouverte ({ id, name })
  const [conflicts, setConflicts] = useState([]);
  const [photos, setPhotos] = useState([]);
  const [uploadKind, setUploadKind] = useState(null);
  const [vDialog, setVDialog] = useState({ open: false, item: null, clientId: null }); // ajout d'un véhicule à la volée
  const beforeRef = useRef(null);
  const afterRef = useRef(null);
  const types = settings?.mission_types || [];
  const empty = { type: "", type_detail: "", client_id: "", vehicle_id: "", location: "", start: "", end: "", employee_id: "", price_ttc: 0, home_pickup: false, notes: "", subscription_id: "" };
  const [form, setForm] = useState(empty);
  const [priceFromGrid, setPriceFromGrid] = useState(false); // le prix affiché vient de la grille du client
  const [priceManual, setPriceManual] = useState(false); // le prix a été tapé à la main : on ne le réécrit plus
  const [priceFromSub, setPriceFromSub] = useState(false); // le prix affiché = valeur d'une visite de l'abonnement
  const [subAuto, setSubAuto] = useState(false); // abonnement pré-sélectionné (seul abonnement actif du client)

  useEffect(() => {
    if (!open) return;
    setPriceFromGrid(false); setPriceManual(false); setPriceFromSub(false); setSubAuto(false);
    api.get("/clients").then((r) => setClients(r.data));
    api.get("/subscriptions").then((r) => setSubs(r.data)).catch(() => setSubs([]));
    api.get("/vehicles").then((r) => setVehicles(r.data));
    if (mission) { setForm({ ...empty, ...mission, subscription_id: mission.subscription_id || "", start: toLocalInput(mission.start), end: toLocalInput(mission.end) }); setPhotos(mission.photos || []); }
    else { setForm({ ...empty, type: types[0] || "", start: toLocalInput(defaultStart), end: "" }); setPhotos([]); }
    setConflicts([]);
    setAvailable(null); // jamais la liste de la mission ouverte juste avant
    setAssigned(null);
    let stale = false;
    const eid = mission?.employee_id;
    if (eid) {
      api.get("/users").then((r) => {
        if (!stale) setAssigned({ id: eid, name: r.data.find((u) => u.id === eid)?.name || "" });
      }).catch(() => { if (!stale) setAssigned({ id: eid, name: "" }); });
    }
    return () => { stale = true; };
  }, [open, mission, defaultStart]);

  // Réglages chargés après l'ouverture : on pré-remplit quand même la première prestation.
  useEffect(() => {
    const first = settings?.mission_types?.[0];
    if (open && !mission && first) setForm((f) => (f.type ? f : { ...f, type: first }));
  }, [open, mission, settings]);

  // Employés libres sur le créneau ; la mission modifiée ne rend pas son propre employé « occupé ».
  useEffect(() => {
    if (!open || !form.start || !form.end || !(new Date(form.end) > new Date(form.start))) return;
    let stale = false; // une réponse arrivée trop tard (créneau ou mission changés) est ignorée
    const params = { start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString() };
    if (mission?.id) params.exclude_id = mission.id;
    api.get("/missions/available-employees", { params })
      .then((r) => { if (!stale) setAvailable(r.data); }).catch(() => { if (!stale) setAvailable([]); });
    return () => { stale = true; };
  }, [open, mission?.id, form.start, form.end]);

  // Employé déjà assigné mais absent de la liste (désactivé, ou occupé sur le nouveau créneau) :
  // on l'affiche quand même, marqué « (indisponible) », au lieu d'un champ vide.
  // Tant que la liste n'est pas arrivée, le nom s'affiche sans la mention.
  const assignedMissing = assigned && !(available || []).some((e) => e.id === assigned.id);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  // Véhicule créé depuis la mission : ajouté à la liste et choisi automatiquement.
  const onVehicleCreated = (v) => { setVehicles((list) => [...list, v]); set("vehicle_id", v.id); };
  const clientVehicles = vehicles.filter((v) => v.client_id === form.client_id);
  // Une mission dont la prestation a été retirée des Réglages garde son type visible dans le menu.
  const typeOptions = form.type && !types.includes(form.type) ? [...types, form.type] : types;

  // Grille tarifaire : uniquement à la création, jamais sur une mission existante (elle garde son prix).
  const applyGrid = (clientId, type) => { if (!priceFromSub) applyGridFresh(clientId, type); };
  const applyGridFresh = (clientId, type) => {
    if (mission || priceManual) return;
    const tarif = clients.find((c) => c.id === clientId)?.tarifs?.find((t) => t.prestation === type);
    if (tarif) { set("price_ttc", tarif.price_ttc); setPriceFromGrid(true); }
    else if (priceFromGrid) { set("price_ttc", 0); setPriceFromGrid(false); }
  };

  // Abonnements proposés : tous les non annulés (prêt possible), plus celui déjà rattaché s'il a été annulé depuis.
  const subOptions = subs.filter((x) => x.status !== "cancelled" || x.id === form.subscription_id);
  const selSub = subs.find((x) => x.id === form.subscription_id);
  const subName = (x) => `${x.client_name || "—"} — ${x.label} (${x.remaining} / ${x.visits})`;
  // Choix d'un abonnement : à la création, le prix devient la valeur d'une visite (information, jamais facturé).
  const applySub = (subId, auto) => {
    set("subscription_id", subId); setSubAuto(auto);
    if (mission || priceManual) return;
    const sub = subs.find((x) => x.id === subId);
    if (sub) { set("price_ttc", sub.unit_price); setPriceFromSub(true); setPriceFromGrid(false); }
    else if (priceFromSub) { set("price_ttc", 0); setPriceFromSub(false); applyGridFresh(form.client_id, form.type); }
  };
  // Changement de client (création) : son seul abonnement actif est pré-sélectionné ; un abonnement choisi à la main (prêt) est gardé.
  const onClientChange = (clientId) => {
    set("client_id", clientId); set("vehicle_id", "");
    if (mission || (form.subscription_id && !subAuto)) return;
    const own = subs.filter((x) => x.client_id === clientId && x.status !== "cancelled");
    if (own.length === 1) { applySub(own[0].id, true); return; }
    if (form.subscription_id) { set("subscription_id", ""); setSubAuto(false); }
    if (priceFromSub) { set("price_ttc", 0); setPriceFromSub(false); }
    if (!priceManual) applyGridFresh(clientId, form.type);
  };

  const uploadPhoto = async (kind, e) => {
    const file = e.target.files?.[0]; e.target.value = "";
    if (!file || !mission) return;
    setUploadKind(kind);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const r = await api.post(`/missions/${mission.id}/photos`, { kind, path: data.path });
      setPhotos(r.data.photos || []); onSaved(); toast.success("Photo ajoutée");
    } catch { toast.error("Échec de l'upload"); } finally { setUploadKind(null); }
  };

  const save = async () => {
    if (!form.type) { toast.error("Choisissez une prestation"); return; }
    if (!form.client_id || !form.start || !form.end) { toast.error("Client, début et fin sont requis"); return; }
    if (form.type === "Autre" && !form.type_detail.trim()) { toast.error("Précisez la prestation"); return; }
    const payload = { ...form, type_detail: form.type === "Autre" ? form.type_detail.trim() : "",
      start: new Date(form.start).toISOString(), end: new Date(form.end).toISOString(), price_ttc: parseFloat(form.price_ttc) || 0 };
    delete payload.photos;
    try {
      if (mission) await api.patch(`/missions/${mission.id}`, payload);
      else await api.post("/missions", payload);
      toast.success(mission ? `${voc.Mission} mise à jour` : `${voc.Mission} créée`);
      setConflicts([]); onSaved(); onOpenChange(false);
    } catch (e) {
      const d = e.response?.data?.detail;
      if (e.response?.status === 409 && d?.conflicts) { setConflicts(d.conflicts); toast.error(d.message); }
      else toast.error(apiError(d) || e.message);
    }
  };

  const PhotoSlot = ({ kind, label, refEl }) => {
    const list = photos.filter((p) => p.kind === kind);
    return (
      <div className="flex-1">
        <div className="flex items-center justify-between mb-2">
          <Label className="text-xs uppercase tracking-overline text-muted-foreground">{label}</Label>
          <input type="file" accept="image/*" ref={refEl} onChange={(e) => uploadPhoto(kind, e)} className="hidden" data-testid={`photo-${kind}-input`} />
          <button type="button" onClick={() => refEl.current?.click()} disabled={uploadKind === kind} className="text-xs text-accent flex items-center gap-1" data-testid={`photo-${kind}-add`}>
            {uploadKind === kind ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Camera className="h-3.5 w-3.5" />} Ajouter
          </button>
        </div>
        <div className="grid grid-cols-2 gap-2">
          {list.length === 0 && <div className="col-span-2 h-20 rounded-xl border border-dashed border-border grid place-items-center text-xs text-muted-foreground">Aucune photo</div>}
          {list.map((p, i) => <div key={i} className="rounded-lg overflow-hidden border border-border" data-testid={`photo-${kind}-${i}`}><ChatImage path={p.path} /></div>)}
        </div>
      </div>
    );
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto rounded-2xl">
        <DialogHeader><DialogTitle className="font-display text-2xl">{mission ? `Modifier la ${voc.mission}` : `Nouvelle ${voc.mission}`}</DialogTitle></DialogHeader>
        <div className="space-y-4 min-w-0">
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2">
              <Label>Type</Label>
              <Select value={form.type} onValueChange={(v) => { set("type", v); applyGrid(form.client_id, v); }}>
                <SelectTrigger data-testid="mission-type"><SelectValue /></SelectTrigger>
                <SelectContent>{typeOptions.map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
              {typeOptions.length === 0 && <p className="text-xs text-muted-foreground">Aucune prestation : ajoutez-en dans Réglages → Types de mission.</p>}
              {form.type === "Autre" && (
                <div className="space-y-2 pt-1">
                  <Label>Préciser la prestation</Label>
                  <Input value={form.type_detail} onChange={(e) => set("type_detail", e.target.value)} data-testid="mission-type-detail" />
                </div>
              )}
            </div>
            <div className="space-y-2">
              <div className="flex items-center gap-2 flex-wrap">
                <Label>Prix TTC (€)</Label>
                {priceFromGrid && <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-emerald-100 text-emerald-700 border border-emerald-200" data-testid="mission-price-badge">Tarif client</span>}
                {priceFromSub && <span className="px-1.5 py-0.5 text-[10px] font-semibold uppercase rounded-full bg-violet-100 text-violet-700 border border-violet-200" data-testid="mission-price-sub-badge">Abonnement</span>}
              </div>
              <Input type="number" step="0.01" value={form.price_ttc} onChange={(e) => { set("price_ttc", e.target.value); setPriceManual(true); setPriceFromGrid(false); setPriceFromSub(false); }} data-testid="mission-price" />
            </div>
          </div>
          <div className="space-y-2">
            <Label>Client</Label>
            <Select value={form.client_id} onValueChange={onClientChange}>
              <SelectTrigger data-testid="mission-client"><SelectValue placeholder="Sélectionner un client" /></SelectTrigger>
              <SelectContent>{clients.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}{c.company ? ` — ${c.company}` : ""}</SelectItem>)}</SelectContent>
            </Select>
            {form.client_id && <SubscriptionBadge summary={clientSubscriptionSummary(subs, form.client_id)} testid="mission-client-sub" />}
          </div>
          {conciergerie && <div className="space-y-2 min-w-0">
            <Label>Abonnement utilisé</Label>
            <Select value={form.subscription_id || "none"} onValueChange={(v) => applySub(v === "none" ? "" : v, false)}>
              <SelectTrigger className="min-w-0 [&>span]:truncate [&>span]:block" data-testid="mission-subscription"><SelectValue placeholder="Aucun" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun (prestation facturée)</SelectItem>
                {subOptions.map((x) => <SelectItem key={x.id} value={x.id}>{subName(x)}{x.status === "cancelled" ? " — annulé" : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {form.subscription_id
              ? <p className="text-xs text-muted-foreground" data-testid="mission-subscription-help">Une visite sera décomptée quand la mission passera « Terminée ». Elle ne sera pas facturée{selSub && form.client_id && selSub.client_id !== form.client_id ? ` (abonnement prêté par ${selSub.client_name})` : ""}.{selSub ? ` Valeur d'une visite : ${money(selSub.unit_price, settings?.currency || "EUR")}.` : ""}</p>
              : subOptions.length === 0 && <p className="text-xs text-muted-foreground">Aucun abonnement en cours.</p>}
          </div>}
          <div className="space-y-2">
            <Label>Véhicule</Label>
            <Select value={form.vehicle_id || "none"} onValueChange={(v) => set("vehicle_id", v === "none" ? "" : v)} disabled={!form.client_id}>
              <SelectTrigger data-testid="mission-vehicle"><SelectValue placeholder="Optionnel" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Aucun</SelectItem>
                {clientVehicles.map((v) => <SelectItem key={v.id} value={v.id}>{v.brand} {v.model}{v.plate ? ` · ${v.plate}` : ""}</SelectItem>)}
              </SelectContent>
            </Select>
            {!form.client_id && <p className="text-xs text-muted-foreground">Choisissez d'abord un client : seuls ses véhicules apparaissent.</p>}
            {form.client_id && clientVehicles.length === 0 && <p className="text-xs text-muted-foreground">Ce client n'a aucun véhicule : ajoutez-en un avec « Nouveau véhicule ».</p>}
            {form.client_id && (
              <button type="button" onClick={() => setVDialog({ open: true, item: null, clientId: form.client_id })} className="text-xs text-accent flex items-center gap-1" data-testid="mission-add-vehicle">
                <Plus className="h-3.5 w-3.5" /> Nouveau véhicule
              </button>
            )}
          </div>
          <div className="space-y-2">
            <Label>Lieu de prise en charge</Label>
            <Input value={form.location} onChange={(e) => set("location", e.target.value)} data-testid="mission-location" />
          </div>
          {conciergerie && <label className="flex items-center gap-2 text-sm cursor-pointer">
            <Checkbox checked={form.home_pickup} onCheckedChange={(v) => set("home_pickup", !!v)} data-testid="mission-home-pickup" />
            Récupération à domicile (+{settings?.home_pickup_fee ?? 20} €)
          </label>}
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-2"><Label>Début</Label>
              <Input type="datetime-local" value={form.start} onChange={(e) => set("start", e.target.value)} data-testid="mission-start" /></div>
            <div className="space-y-2"><Label>Fin</Label>
              <Input type="datetime-local" value={form.end} onChange={(e) => set("end", e.target.value)} data-testid="mission-end" /></div>
          </div>
          <div className="space-y-2">
            <Label>{`${voc.Employee} disponible`}</Label>
            <Select value={form.employee_id || "none"} onValueChange={(v) => set("employee_id", v === "none" ? "" : v)}>
              <SelectTrigger data-testid="mission-employee"><SelectValue placeholder="Non assigné" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Non assigné</SelectItem>
                {assignedMissing && <SelectItem value={assigned.id}>{assigned.name || voc.Employee}{available ? " (indisponible)" : ""}</SelectItem>}
                {(available || []).map((e) => <SelectItem key={e.id} value={e.id}>{e.name}</SelectItem>)}
              </SelectContent>
            </Select>
            <p className="text-xs text-muted-foreground">{`Seuls les ${voc.employees} libres sur ce créneau apparaissent.`}</p>
          </div>
          {conflicts.length > 0 && (
            <div className="p-3 rounded-xl border border-destructive/40 bg-destructive/10 text-sm" data-testid="conflict-alert">
              <div className="flex items-center gap-2 font-medium text-destructive"><AlertTriangle className="h-4 w-4" /> Conflit d'horaire</div>
              {conflicts.map((c) => <div key={c.id} className="text-muted-foreground mt-1">{missionLabel(c)} · {fmtDateTime(c.start)} → {fmtDateTime(c.end)}</div>)}
            </div>
          )}
          <div className="space-y-2"><Label>Notes / instructions</Label>
            <Textarea value={form.notes} onChange={(e) => set("notes", e.target.value)} data-testid="mission-notes" /></div>

          {mission && (
            <div className="pt-2 border-t border-border">
              <Label className="text-sm font-semibold flex items-center gap-2 mb-3"><Camera className="h-4 w-4 text-accent" /> Photos avant / après</Label>
              <div className="flex gap-4">
                <PhotoSlot kind="before" label="Avant" refEl={beforeRef} />
                <PhotoSlot kind="after" label="Après" refEl={afterRef} />
              </div>
            </div>
          )}
        </div>
        <VehicleDialog state={vDialog} setState={setVDialog} onSaved={onVehicleCreated} />
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-full">Annuler</Button>
          <Button onClick={save} data-testid="mission-save" className="rounded-full">Enregistrer</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
